/**
 * @fileoverview Tooltip overlay for the Period Calculator.
 * Shows a spotlight effect on the wheel with instructional text.
 * @module period-tracker/period-tooltip
 */

/* global femcare */

femcare.periodTracker = femcare.periodTracker || {};

/**
 * @namespace femcare.periodTracker.tooltip
 * @description Manages the first-time user tooltip overlay.
 */
femcare.periodTracker.tooltip = {
    element: null,
    closeButton: null,
    card: null,
    circularViewElement: null,
    wheelContainer: null,
    _resizeListener: null,
    _documentClickListener: null,
    _closeButtonListener: null,
    _closeButtonTouchListener: null,
    _keydownListener: null,
    _touchListenerOptions: { passive: false },

    /**
     * Initializes the tooltip module.
     * @param {Object} elements - DOM element references
     * @param {HTMLElement} elements.circularView - Circular view element
     * @param {HTMLElement} elements.wheelContainer - Wheel container element
     * @returns {boolean} True if initialization successful
     */
    init: function (elements) {
        this.circularViewElement = elements.circularView;
        this.wheelContainer = elements.wheelContainer;

        if (!this.cacheElements()) {
            return false;
        }

        return true;
    },

    /**
     * Caches tooltip DOM element references.
     * @returns {boolean} True if tooltip element found
     */
    cacheElements: function () {
        const selectors = femcare.periodTracker.config.selectors;
        this.element = document.querySelector(selectors.tooltip);

        if (!this.element) {
            return false;
        }

        this.closeButton = this.element.querySelector('.period-calendar-tooltip__close');
        this.card = this.element.querySelector('.period-calendar-tooltip__card');
        return true;
    },

    /**
     * Shows the tooltip if not previously seen.
     */
    showIfFirstTime: function () {
        const storageKey = femcare.periodTracker.config.storageKeys.tooltipSeen;
        const tooltipSeen = sessionStorage.getItem(storageKey);

        if (!tooltipSeen) {
            const delay = femcare.periodTracker.config.timing.tooltipDelay;
            setTimeout(() => this.show(), delay);
        }
    },

    /**
     * Shows the tooltip overlay.
     */
    show: function () {
        if (!this.element || !this.wheelContainer) {
            return;
        }

        this.element.classList.remove('hidden');
        this.bindEvents();

        requestAnimationFrame(() => {
            this.updatePosition();
            document.body.style.overflow = 'hidden';

            if (this.closeButton) {
                this.closeButton.focus();
            }
        });
    },

    /**
     * Hides the tooltip and marks as seen.
     */
    hide: function () {
        if (!this.element) {
            return;
        }

        if (!this.element.classList.contains('hidden')) {
            const storageKey = femcare.periodTracker.config.storageKeys.tooltipSeen;
            sessionStorage.setItem(storageKey, '1');
        }

        document.body.style.overflow = '';
        this.element.classList.add('hidden');

        this.removeListeners();
    },

    /**
     * Updates tooltip position based on wheel location.
     */
    updatePosition: function () {
        if (!this.element || !this.card || !this.circularViewElement || !this.wheelContainer) {
            return;
        }

        const wheelRect = this.wheelContainer.getBoundingClientRect();
        const spotlightY = wheelRect.top + (wheelRect.height / 2);

        const circularViewRect = this.circularViewElement.getBoundingClientRect();
        const cardHeight = this.card.offsetHeight;
        const arrowHeight = 14;
        this.element.style.setProperty('--spotlight-y', `${spotlightY}px`);
        this.element.style.setProperty('--card-y', `${circularViewRect.top - cardHeight - arrowHeight}px`);
    },

    /**
     * Binds tooltip event listeners.
     */
    bindEvents: function () {
        if (!this.element || this._documentClickListener) {
            return;
        }

        if (this.closeButton) {
            this._closeButtonListener = (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.hide();
            };
            this.closeButton.addEventListener('click', this._closeButtonListener);
            this._closeButtonTouchListener = (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.hide();
            };
            this.closeButton.addEventListener('touchstart', this._closeButtonTouchListener, this._touchListenerOptions);

            this._keydownListener = (e) => {
                if (e.key === 'Tab' || e.key === 'Escape') {
                    e.preventDefault();
                    this.hide();
                }
            };
            this.closeButton.addEventListener('keydown', this._keydownListener);
        }

        this._documentClickListener = (event) => {
            if (this.card && !this.card.contains(event.target)) {
                event.preventDefault();
                event.stopPropagation();
                this.hide();
            }
        };

        document.addEventListener('mousedown', this._documentClickListener);
        document.addEventListener('touchstart', this._documentClickListener, this._touchListenerOptions);

        this._resizeListener = () => this.updatePosition();
        window.addEventListener('resize', this._resizeListener);
    },

    /**
     * Removes event listeners.
     */
    removeListeners: function () {
        if (this._resizeListener) {
            window.removeEventListener('resize', this._resizeListener);
            this._resizeListener = null;
        }

        if (this._documentClickListener) {
            document.removeEventListener('mousedown', this._documentClickListener);
            document.removeEventListener('touchstart', this._documentClickListener, this._touchListenerOptions);

            this._documentClickListener = null;
        }

        if (this._closeButtonListener && this.closeButton) {
            this.closeButton.removeEventListener('click', this._closeButtonListener);
            this._closeButtonListener = null;
        }

        if (this._closeButtonTouchListener && this.closeButton) {
            this.closeButton.removeEventListener('touchstart', this._closeButtonTouchListener, this._touchListenerOptions);
            this._closeButtonTouchListener = null;
        }

        if (this._keydownListener && this.closeButton) {
            this.closeButton.removeEventListener('keydown', this._keydownListener);
            this._keydownListener = null;
        }
    },

    /**
     * Checks if tooltip is currently visible.
     * @returns {boolean} True if visible
     */
    isVisible: function () {
        return this.element && !this.element.classList.contains('hidden');
    }
};
